/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DecimalString, RecordedReason } from '../../api';
import type { BalanceHold, Invoice, JournalEntry, Payment, Posting } from '../../api/areas/commerce';
import { DataTable, Pager as BlockPager, type Column } from '../../components/blocks';
import { EntityRef } from '../../components/EntityRef';
import { useJournalDescription } from '../../components/SystemReason';
import { EmptyState } from '../../components/States';
import { formatDate, formatDateTime, formatMoney, formatRelative, isZeroAmount } from '../../lib/format';
import {
  EnumBadge,
  EnumText,
  FilterChip,
  FilterSelect as BaseFilterSelect,
  FilterText as BaseFilterText,
  RefreshButton,
  ResetFilters,
  RichText as BaseRichText,
  useEnumLabel,
  type ToneMap,
} from '../accounts/shared';

export { FilterChip, RefreshButton, ResetFilters, useEnumLabel };

export const ORDER_STATUS: ToneMap = {
  PLACED: 'pending',
  FUNDS_RESERVED: 'pending',
  FULFILLING: 'pending',
  COMPLETED: 'ok',
  PARTIALLY_COMPLETED: 'warn',
  FAILED: 'crit',
  CANCELLED: 'neutral',
};

export const ORDER_ITEM_STATUS: ToneMap = {
  PENDING: 'pending',
  FUNDS_RESERVED: 'pending',
  FULFILLING: 'pending',
  FULFILLED: 'ok',
  FAILED: 'crit',
  REFUNDED: 'info',
  CANCELLED: 'neutral',
};

export const HOLD_STATUS: ToneMap = { ACTIVE: 'pending', CAPTURED: 'ok', RELEASED: 'neutral', EXPIRED: 'warn' };

/** UNKNOWN is never shown as failed: it waits for reconciliation. */
export const PAYMENT_STATUS: ToneMap = { INITIATED: 'neutral', PENDING: 'pending', SUCCEEDED: 'ok', FAILED: 'crit', UNKNOWN: 'serious', CANCELLED: 'neutral' };

/** An attempt at a gateway's page; UNKNOWN is polled, never a failure. */
export const ATTEMPT_STATUS: ToneMap = {
  CREATED: 'neutral',
  PENDING: 'pending',
  REQUIRES_ACTION: 'pending',
  AUTHORIZED: 'info',
  CAPTURED: 'ok',
  FAILED: 'crit',
  CANCELLED: 'neutral',
  EXPIRED: 'neutral',
  UNKNOWN: 'serious',
};

export const REFUND_STATUS: ToneMap = { COMPLETED: 'ok', PENDING_PROVIDER: 'pending', FAILED: 'crit' };

export const DOC_TYPE: ToneMap = { INVOICE: 'info', CREDIT_NOTE: 'warn' };

export const ACCOUNT_STATUS: ToneMap = { OPEN: 'ok', FROZEN: 'warn', CLOSED: 'neutral' };

/** Translated status badge from `commerce:enum.<group>`; raw code in the tooltip. */
export function Status({ group, tones, value }: { group: string; tones: ToneMap; value: string | null | undefined }) {
  return <EnumBadge ns="commerce" group={group} tones={tones} value={value} />;
}

/** Translated enum text from `commerce:enum.<group>`; raw code in the tooltip. */
export function Label({ group, value }: { group: string; value: string | null | undefined }) {
  return <EnumText ns="commerce" group={group} value={value} />;
}

export function RichText({ k, values }: { k: string; values?: Record<string, unknown> }) {
  return <BaseRichText ns="commerce" k={k} values={values} />;
}

/** Exact amount; the raw decimal string is in the tooltip. */
export function Amount({ amount, currency, signed, className }: { amount: DecimalString | null | undefined; currency: string | null | undefined; signed?: 'DEBIT' | 'CREDIT'; className?: string }) {
  if (amount === null || amount === undefined || !currency) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={`tabular whitespace-nowrap ${className ?? ''}`} title={`${amount} ${currency}`}>
      {signed === 'CREDIT' ? '−' : ''}
      {formatMoney({ amount, currency })}
    </span>
  );
}

export function isZero(amount: DecimalString): boolean {
  return isZeroAmount(amount) === true;
}

export const orderPath = (id: string) => `/orders/${encodeURIComponent(id)}`;
export const quotePath = (id: string) => `/quotes/${encodeURIComponent(id)}`;
export const paymentPath = (id: string) => `/payments/${encodeURIComponent(id)}`;
export const refundPath = (id: string) => `/refunds/${encodeURIComponent(id)}`;
export const invoicePath = (id: string) => `/invoices/${encodeURIComponent(id)}`;
export const holdPath = (id: string) => `/holds/${encodeURIComponent(id)}`;
export const accountPath = (id: string) => `/ledger/accounts/${encodeURIComponent(id)}`;
export const journalPath = (id: string) => `/ledger/journal/${encodeURIComponent(id)}`;
export const priceBookPath = (id: string) => `/pricing/books/${encodeURIComponent(id)}`;
export const promotionPath = (id: string) => `/promotions/${encodeURIComponent(id)}`;
export const customerPath = (id: string) => `/customers/${encodeURIComponent(id)}`;

/** An issued document's number, e.g. `INV-42`. */
export const docNumber = (i: { series: string; number: number }) => `${i.series}-${i.number}`;

export const cellLink = 'font-medium text-primary hover:underline';
export const mutedSmall = 'text-xs text-muted-foreground';

const dash = <span className="text-muted-foreground">—</span>;

/** The kinds of account in the chart of accounts (codes are stable: `crates/billing` `accounts`). */
export type AccountKind = 'WALLET' | 'CLEARING' | 'REVENUE' | 'COST_OF_SALES' | 'REGISTRY_PAYABLE' | 'REFUNDS_PAYABLE' | 'TAX_PAYABLE' | 'OTHER';

/** Reads the kind (and what it is for: a provider, a registry, a tax code) out of an account code. */
export function accountKind(code: string): { kind: AccountKind; qualifier: string | null } {
  const p = code.split(':');
  if (p[0] === 'customer' && p[2] === 'wallet') return { kind: 'WALLET', qualifier: null };
  if (p[0] === 'registry' && p[2] === 'payable') return { kind: 'REGISTRY_PAYABLE', qualifier: p[1] ?? null };
  if (p[0] === 'platform') {
    if (p[1] === 'clearing') return { kind: 'CLEARING', qualifier: p[2] ?? null };
    if (p[1] === 'revenue') return { kind: 'REVENUE', qualifier: null };
    if (p[1] === 'cost_of_sales') return { kind: 'COST_OF_SALES', qualifier: null };
    if (p[1] === 'refunds_payable') return { kind: 'REFUNDS_PAYABLE', qualifier: null };
    if (p[1] === 'tax_payable') return { kind: 'TAX_PAYABLE', qualifier: p[2] ?? null };
  }
  return { kind: 'OTHER', qualifier: null };
}

/**
 * A ledger account in words: the owner's name for a wallet, what the account is for otherwise.
 * The code itself (which embeds a customer id) is never the visible name.
 */
export function useAccountName() {
  const enumLabel = useEnumLabel('commerce');
  return (a: { code: string; owner_name?: string | null; currency?: string | null }): { label: string; sub: string } => {
    const { kind, qualifier } = accountKind(a.code);
    const kindLabel = kind === 'OTHER' ? a.code : enumLabel('accountKind', kind);
    const label = a.owner_name ?? kindLabel;
    const sub = [a.owner_name ? kindLabel : qualifier, a.currency].filter(Boolean).join(' · ');
    return { label, sub };
  };
}

/** A ledger account, by name, linking to its page; the code stays in the tooltip. */
export function AccountRef({ id, code, ownerName, currency, compact }: { id: string; code: string; ownerName?: string | null; currency?: string | null; compact?: boolean }) {
  const name = useAccountName();
  const { label, sub } = name({ code, owner_name: ownerName, currency });
  return <EntityRef label={label} sub={compact ? undefined : sub || undefined} id={code} to={accountPath(id)} className={compact ? 'truncate text-sm' : undefined} />;
}

export function CustomerRef({ id, name, sub }: { id: string | null | undefined; name: string | null | undefined; sub?: ReactNode }) {
  if (!id || !name) return dash;
  return <EntityRef label={name} sub={sub} id={id} to={customerPath(id)} />;
}

/**
 * The owner of a ledger account or a hold, by name, linking to their page when they have one.
 * A registry or platform owner must never be linked as if it were a customer.
 */
export function AccountOwner({ account: a }: { account: { owner_type: string | null; owner_id: string | null; owner_name: string | null } }) {
  const { t } = useTranslation('commerce');
  if (a.owner_type === 'CUSTOMER') return <CustomerRef id={a.owner_id} name={a.owner_name} />;
  if (a.owner_type) return <span>{a.owner_name ?? <Label group="ownerType" value={a.owner_type} />}</span>;
  return <span className="text-muted-foreground">{t('common.platform')}</span>;
}

/**
 * An order has no number in the schema, so it is named by what it is for: the first name on it,
 * `+N` for the rest, and the date it was placed — `example.vn +2 · 12 Sep 2026`.
 */
export function orderLabel({ domain, count, at, fallback }: { domain?: string | null; count?: number; at?: string | null; fallback: string }): { label: string; sub?: string } {
  const extra = (count ?? 1) - 1;
  const sub = [extra > 0 ? `+${extra}` : null, at ? formatDate(at) : null].filter(Boolean).join(' · ');
  return { label: domain ?? fallback, sub: sub || undefined };
}

/** An order, shown by the names on it and when it was placed. */
export function OrderRef({ id, domain, count, at }: { id: string | null | undefined; domain?: string | null; count?: number; at?: string | null }) {
  const { t } = useTranslation('commerce');
  if (!id) return dash;
  const { label, sub } = orderLabel({ domain, count, at, fallback: t('common.order') });
  return <EntityRef label={label} sub={sub} id={id} to={orderPath(id)} />;
}

export function QuoteRef({ id, domain, sub }: { id: string | null | undefined; domain?: string | null; sub?: ReactNode }) {
  const { t } = useTranslation('commerce');
  if (!id) return dash;
  return <EntityRef label={domain ?? t('quote.title')} sub={sub} id={id} to={quotePath(id)} />;
}

/** A journal entry, shown by its description: the platform's words in the operator's language (a reversal's: the refund's reason; a wallet adjustment's: the staff member's text, by `entryType`). */
export function JournalRef({ id, description, entryType }: { id: string | null | undefined; description?: RecordedReason | null; entryType?: string | null }) {
  const { t } = useTranslation('commerce');
  const words = useJournalDescription();
  if (!id) return dash;
  return <EntityRef label={words(description, entryType).text || t('common.entry')} id={id} to={journalPath(id)} />;
}

/** A payment, shown by the reference the provider gave it. */
export function PaymentRef({ payment }: { payment: Pick<Payment, 'id' | 'provider' | 'provider_reference'> | null | undefined }) {
  const { t } = useTranslation('commerce');
  if (!payment) return dash;
  return <EntityRef label={payment.provider_reference ?? t('common.noReference')} sub={payment.provider} id={payment.id} to={paymentPath(payment.id)} className="font-mono" />;
}

export function InvoiceRef({ invoice }: { invoice: Pick<Invoice, 'id' | 'series' | 'number'> }) {
  return <EntityRef label={docNumber(invoice)} id={invoice.id} to={invoicePath(invoice.id)} className="font-mono" />;
}

/** A refund, shown by the name it refunds. */
export function RefundRef({ id, domain, sub }: { id: string; domain?: string | null; sub?: ReactNode }) {
  const { t } = useTranslation('commerce');
  return <EntityRef label={domain ?? t('payments.refundNoName')} sub={sub} id={id} to={refundPath(id)} />;
}

/** A balance hold, shown by the name its funds are reserved for. */
export function HoldRef({ hold }: { hold: Pick<BalanceHold, 'id' | 'domain_name' | 'purpose' | 'resource_type'> }) {
  const enumLabel = useEnumLabel('commerce');
  const purpose = enumLabel('holdPurpose', hold.purpose);
  return <EntityRef label={hold.domain_name ?? purpose} sub={hold.domain_name ? purpose : enumLabel('resourceType', hold.resource_type)} id={hold.id} to={holdPath(hold.id)} />;
}

/** What a journal entry was posted for, by name, linking to that record's page. */
export function ResourceRef({ entry }: { entry: Pick<JournalEntry, 'resource_type' | 'resource_id' | 'resource_label' | 'resource_order_id'> }) {
  const enumLabel = useEnumLabel('commerce');
  const words = useJournalDescription();
  const { resource_type: type, resource_id: id, resource_label: recorded, resource_order_id: order } = entry;
  if (!type || !id) return dash;
  // a reversal's resource is the entry it corrects: its description in words; any other, a name as it is
  const label = (type === 'journal_entry' ? words(recorded).text : typeof recorded === 'string' ? recorded : '') || null;
  const kind = enumLabel('resourceType', type);
  const to =
    type === 'payment'
      ? paymentPath(id)
      : type === 'journal_entry'
        ? journalPath(id)
        : type === 'balance_hold'
          ? holdPath(id)
          : type === 'customer'
            ? customerPath(id)
            : order
              ? orderPath(order)
              : undefined;
  return <EntityRef label={label ?? kind} sub={label ? kind : undefined} id={id} to={to} />;
}

export function When({ at }: { at: string | null | undefined }) {
  if (!at) return dash;
  return (
    <span className="whitespace-nowrap" title={formatDateTime(at)}>
      {formatRelative(at)}
    </span>
  );
}

/** Keyset pager: the cursor stack lets the operator step back to newer pages. */
export function Pager({ cursors, next, onNewer, onOlder }: { cursors: string[]; next: string | null; onNewer: () => void; onOlder: (cursor: string) => void }) {
  const { t } = useTranslation('commerce');
  if (!cursors.length && !next) return null;
  return (
    <BlockPager
      canPrevious={cursors.length > 0}
      canNext={Boolean(next)}
      onPrevious={onNewer}
      onNext={() => next && onOlder(next)}
      previousLabel={t('pager.newer')}
      nextLabel={t('pager.older')}
      info={t('pager.page', { n: cursors.length + 1 })}
    />
  );
}

/** Postings of one journal entry: debits and credits side by side. */
export function PostingsTable({ postings, compact }: { postings: Posting[]; compact?: boolean }) {
  const { t } = useTranslation('commerce');
  const columns: Column<Posting>[] = [
    {
      key: 'account',
      header: t('common.account'),
      cell: (p) => <AccountRef id={p.account_id} code={p.account_code} ownerName={p.account_owner_name} currency={p.currency} />,
    },
    ...(compact ? [] : [{ key: 'type', header: t('common.type'), cell: (p: Posting) => <Label group="accountType" value={p.account_type} /> }]),
    { key: 'debit', header: t('common.debit'), numeric: true, cell: (p) => (p.direction === 'DEBIT' ? <Amount amount={p.amount} currency={p.currency} /> : null) },
    { key: 'credit', header: t('common.credit'), numeric: true, cell: (p) => (p.direction === 'CREDIT' ? <Amount amount={p.amount} currency={p.currency} /> : null) },
  ];
  return <DataTable columns={columns} rows={postings} rowKey={(p) => p.id} />;
}

/** Journal entries with their postings inline; the description opens the entry. */
export function JournalTable({ entries, empty }: { entries: JournalEntry[]; empty?: ReactNode }) {
  const { t } = useTranslation('commerce');
  const columns: Column<JournalEntry>[] = [
    {
      key: 'description',
      header: t('common.description'),
      className: 'whitespace-normal',
      cell: (e) => (
        <span className="block min-w-48">
          <JournalRef id={e.id} description={e.description} entryType={e.entry_type} />
        </span>
      ),
    },
    {
      key: 'type',
      header: t('common.type'),
      className: 'whitespace-nowrap',
      cell: (e) => (
        <>
          <Label group="entryType" value={e.entry_type} />
          {e.reversed_by_entry_id ? <div className={mutedSmall}>{t('common.reversed')}</div> : null}
        </>
      ),
    },
    { key: 'resource', header: t('common.resource'), cell: (e) => <ResourceRef entry={e} /> },
    {
      key: 'postings',
      header: t('common.postings'),
      cell: (e) => (
        <ul className="space-y-0.5 text-xs">
          {e.postings.map((p) => (
            <li key={p.id} className="flex items-baseline gap-1.5 whitespace-nowrap">
              <span className="w-6 shrink-0 font-semibold text-muted-foreground" title={p.direction}>
                {p.direction === 'DEBIT' ? t('common.debitShort') : t('common.creditShort')}
              </span>
              <span className="max-w-40 truncate">
                <AccountRef id={p.account_id} code={p.account_code} ownerName={p.account_owner_name} currency={p.currency} compact />
              </span>
              <Amount amount={p.amount} currency={p.currency} />
            </li>
          ))}
        </ul>
      ),
    },
    { key: 'posted', header: t('common.posted'), cell: (e) => <When at={e.posted_at} /> },
  ];
  return <DataTable columns={columns} rows={entries} rowKey={(e) => e.id} empty={empty ?? <EmptyState title={t('common.noJournal')} />} />;
}

/** Updates one search param, dropping it when empty and resetting the page cursor. */
export function setParam(params: URLSearchParams, key: string, value: string): URLSearchParams {
  const p = new URLSearchParams(params);
  if (value) p.set(key, value);
  else p.delete(key);
  return p;
}

/** Drops the given search params (a FilterBar "Reset"). */
export function clearParams(params: URLSearchParams, keys: readonly string[]): URLSearchParams {
  const p = new URLSearchParams(params);
  for (const k of keys) p.delete(k);
  return p;
}

/** Keyset cursor stack, reset whenever `resetKey` (the serialised filters) changes. */
export function useKeyset(resetKey: string) {
  const [state, setState] = useState<{ key: string; stack: string[] }>({ key: resetKey, stack: [] });
  const stack = state.key === resetKey ? state.stack : [];
  return {
    before: stack[stack.length - 1],
    cursors: stack,
    older: (cursor: string) => setState({ key: resetKey, stack: [...stack, cursor] }),
    newer: () => setState({ key: resetKey, stack: stack.slice(0, -1) }),
  };
}

/** FilterBar select bound to a search param; option labels come from `commerce:enum.<group>`. */
export function FilterSelect({ label, value, options, onChange, group, className }: { label: string; value: string; options: readonly string[]; onChange: (v: string) => void; group: string; className?: string }) {
  const enumLabel = useEnumLabel('commerce');
  return <BaseFilterSelect label={label} value={value} options={options.map((o) => [o, enumLabel(group, o)] as const)} onChange={onChange} className={className} />;
}

/** FilterBar text filter applied on Enter or blur. */
export function FilterText({ label, value, onChange, placeholder, className }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return <BaseFilterText label={label} value={value} onApply={onChange} placeholder={placeholder} className={className} />;
}

/**
 * The "filtered to one customer" chip. The name comes from the rows on screen, so the customer
 * id in the link never has to be shown.
 */
export function CustomerFilterChip({ name, onClear }: { name: string | undefined; onClear: () => void }) {
  const { t } = useTranslation('commerce');
  return <FilterChip label={name ? t('filter.customer', { name }) : t('filter.customerUnknown')} onClear={onClear} />;
}

/** Number of records in the given statuses, across currencies. */
export function countFor<S extends string>(rows: readonly { status: S; count: number }[], statuses: readonly S[]): number {
  return rows.filter((r) => statuses.includes(r.status)).reduce((n, r) => n + r.count, 0);
}
